package com.ijoin.ui.components

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.QrCode2
import androidx.compose.material3.*
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import com.ijoin.data.model.Ticket
import com.ijoin.platform.createTimeZone
import com.ijoin.ui.design.EventCardCorner
import com.ijoin.ui.design.EventCardSurface
import com.ijoin.ui.design.EventPill
import com.ijoin.ui.design.eventTicketGradient
import kotlinx.datetime.Instant
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime

@Composable
fun TicketCard(
    ticket: Ticket,
    onTicketClick: (String) -> Unit,
    onFavoriteClick: (String) -> Unit,
    modifier: Modifier = Modifier
) {
    val isHighlight = ticket.eventTitle?.contains("VIP", ignoreCase = true) == true ||
        ticket.eventTitle?.contains("Jazz", ignoreCase = true) == true

    Card(
        modifier = modifier
            .width(320.dp)
            .height(160.dp)
            .clickable { onTicketClick(ticket.id) },
        shape = RoundedCornerShape(EventCardCorner),
        elevation = CardDefaults.cardElevation(defaultElevation = 4.dp)
    ) {
        Box(
            modifier = Modifier
                .fillMaxSize()
                .background(
                    brush = eventTicketGradient(isHighlight)
                )
        ) {
            Column(
                modifier = Modifier
                    .fillMaxSize()
                    .padding(20.dp),
                verticalArrangement = Arrangement.SpaceBetween
            ) {
                // Top section with badge and QR icon
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.Top
                ) {
                    EventPill(
                        text = if (ticket.eventTitle?.contains("VIP", ignoreCase = true) == true) "VIP" else "TICKET",
                        backgroundColor = EventCardSurface.copy(alpha = 0.28f),
                        textColor = EventCardSurface,
                        textStyle = MaterialTheme.typography.labelSmall,
                        fontWeight = FontWeight.Bold,
                        contentPadding = PaddingValues(horizontal = 8.dp, vertical = 4.dp)
                    )

                    Icon(
                        imageVector = Icons.Default.QrCode2,
                        contentDescription = "QR Code",
                        tint = EventCardSurface,
                        modifier = Modifier.size(28.dp)
                    )
                }

                // Event title
                Text(
                    text = ticket.eventTitle ?: "Event",
                    style = MaterialTheme.typography.titleLarge,
                    color = EventCardSurface,
                    fontWeight = FontWeight.Bold,
                    maxLines = 2,
                    overflow = TextOverflow.Ellipsis
                )

                // Date and location
                Column(
                    verticalArrangement = Arrangement.spacedBy(4.dp)
                ) {
                    Text(
                        text = formatTicketDate(ticket.primaryDateForDisplay(), ticket.eventTimeZone),
                        style = MaterialTheme.typography.bodyMedium,
                        color = EventCardSurface.copy(alpha = 0.9f)
                    )

                    ticket.eventLocation?.let { location ->
                        Text(
                            text = location,
                            style = MaterialTheme.typography.bodySmall,
                            color = EventCardSurface.copy(alpha = 0.8f),
                            maxLines = 1,
                            overflow = TextOverflow.Ellipsis
                        )
                    }
                }
            }
        }
    }
}

private fun formatTicketDate(instant: Instant?, timeZone: String? = null): String {
    if (instant == null) return ""

    return try {
        val zone = if (!timeZone.isNullOrBlank()) createTimeZone(timeZone) else TimeZone.UTC
        val dateTime = instant.toLocalDateTime(zone)
        val date = dateTime.date
        val time = dateTime.time

        val month = getMonthAbbr(date.monthNumber)
        val day = date.dayOfMonth

        // Convert 24h to 12h format
        val hour = time.hour
        val minute = time.minute.toString().padStart(2, '0')
        val amPm = if (hour >= 12) "PM" else "AM"
        val hour12 = when {
            hour == 0 -> 12
            hour > 12 -> hour - 12
            else -> hour
        }

        "$month $day, $hour12:$minute $amPm"
    } catch (e: Exception) {
        instant.toString().take(10)
    }
}

private fun Ticket.primaryDateForDisplay(): Instant? {
    return eventDate ?: occurrenceDateTime ?: createdAt
}

private fun getMonthAbbr(month: Int): String {
    return when (month) {
        1 -> "Jan"
        2 -> "Feb"
        3 -> "Mar"
        4 -> "Apr"
        5 -> "May"
        6 -> "Jun"
        7 -> "Jul"
        8 -> "Aug"
        9 -> "Sep"
        10 -> "Oct"
        11 -> "Nov"
        12 -> "Dec"
        else -> ""
    }
}
